import React, { useEffect, useMemo, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Heart, Search, ShoppingBag, User, Menu, ChevronDown } from "lucide-react";
import PhoneOtpModal from "../auth/PhoneOtpModal";
import { useAuth } from "../../context/AuthContext";
import { useCart } from "../../context/CartContext";
import { useBranding } from "../../context/BrandingContext";
import { useFeatures } from "../../context/FeaturesContext";
import { api, imgUrl } from "../../lib/api";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "../ui/sheet";
import { getHeaderStyles } from "../../theme/storeStyles";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";

// Curated quick links shown alongside categories in the mega menu
const QUICK_LINKS = {
  women: [
    { label: "New Arrivals", to: "/shop?sort=new" },
    { label: "Best Sellers", to: "/shop?sort=rating" },
    { label: "Under ₹5,000", to: "/shop?max_price=5000" },
    { label: "Bridal Collection", to: "/shop/lehengas" },
  ],
  men: [
    { label: "New Arrivals", to: "/shop/menswear?sort=new" },
    { label: "Sherwanis", to: "/shop/menswear" },
    { label: "Wedding Picks", to: "/shop/menswear?occasion=Wedding" },
  ],
  kids: [
    { label: "New Arrivals", to: "/shop?sort=new" },
    { label: "Festive Wear", to: "/shop?occasion=Festive" },
  ],
  accessories: [
    { label: "All Jewelry", to: "/shop/jewelry" },
    { label: "Bridal Sets", to: "/shop/jewelry?occasion=Bridal" },
  ],
};

export default function StoreHeader() {
  const [openAuth, setOpenAuth] = useState(false);
  const [openMenu, setOpenMenu] = useState(false);
  const [q, setQ] = useState("");
  const [tree, setTree] = useState([]);
  const [hovered, setHovered] = useState(null);
  const { user, logout } = useAuth();
  const { cartCount, wishlist } = useCart();
  const { branding } = useBranding();
  const { has: hasFeature } = useFeatures();
  const reelsEnabled = hasFeature("social_reels");
  const navigate = useNavigate();
  const wishlistCount = wishlist?.product_ids?.length || 0;
  const styles = getHeaderStyles(branding?.theme_preset);

  useEffect(() => {
    let alive = true;
    api.get("/catalog/tree")
      .then(({ data }) => alive && setTree(data?.tree || []))
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const departments = useMemo(() => tree || [], [tree]);

  const onSearch = (e) => {
    e.preventDefault();
    if (q.trim()) {
      navigate(`/shop?q=${encodeURIComponent(q.trim())}`);
      setQ("");
      setOpenMenu(false);
    }
  };

  const onLogout = () => {
    logout();
    navigate("/");
  };

  const renderMegaPanel = (dept) => {
    const direct = (dept.children || []).filter((c) => (c.children?.length || 0) === 0);
    const grouped = (dept.children || []).filter((c) => (c.children?.length || 0) > 0);
    const quick = QUICK_LINKS[dept.slug] || [];
    return (
      <div
        className="absolute left-0 right-0 top-full bg-white border-t border-[hsl(var(--kb-border))] shadow-xl"
        onMouseEnter={() => setHovered(dept.slug)}
        onMouseLeave={() => setHovered(null)}
        data-testid={`mega-panel-${dept.slug}`}
      >
        <div className="max-w-7xl mx-auto grid grid-cols-12 gap-8 px-8 lg:px-16 py-10">
          {grouped.length > 0 ? (
            grouped.map((g) => (
              <div key={g.id} className="col-span-3" data-testid={`mega-group-${g.slug}`}>
                <Link
                  to={`/shop/${g.slug}`}
                  onClick={() => setHovered(null)}
                  className="overline mb-4 text-[hsl(var(--kb-primary))] hover:underline block"
                  data-testid={`mega-link-${g.slug}`}
                >
                  {g.name}
                </Link>
                <ul className="space-y-2.5 text-sm">
                  {(g.children || []).slice(0, 8).map((c) => (
                    <li key={c.id}>
                      <Link
                        to={`/shop/${c.slug}`}
                        onClick={() => setHovered(null)}
                        className="hover:text-[hsl(var(--kb-primary))] transition-colors"
                        data-testid={`mega-link-${c.slug}`}
                      >
                        {c.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))
          ) : (
            <div className="col-span-9 grid grid-cols-3 gap-x-8 gap-y-3">
              {direct.slice(0, 12).map((c) => (
                <Link
                  key={c.id}
                  to={`/shop/${c.slug}`}
                  onClick={() => setHovered(null)}
                  className="text-sm hover:text-[hsl(var(--kb-primary))] transition-colors"
                  data-testid={`mega-link-${c.slug}`}
                >
                  {c.name}
                </Link>
              ))}
            </div>
          )}
          <div className="col-span-3 border-l border-[hsl(var(--kb-border))] pl-8">
            <div className="overline mb-4 text-[hsl(var(--kb-primary))]">Quick Picks</div>
            <ul className="space-y-2.5 text-sm">
              {quick.map((q2) => (
                <li key={q2.to}>
                  <Link
                    to={q2.to}
                    onClick={() => setHovered(null)}
                    className="hover:text-[hsl(var(--kb-primary))] transition-colors"
                    data-testid={`mega-quick-${q2.label.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {q2.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    );
  };

  return (
    <header
      data-testid="store-header"
      data-theme-preset={branding?.theme_preset || "kalki"}
      className={`${styles.root} safe-pl safe-pr`}
    >
      {styles.showAnnouncement ? (
        <div className={`${styles.announcementBar} text-center py-2 px-4 safe-area-top`}>
          <span className="hidden sm:inline">Complimentary shipping on orders above ₹1,999 · Crafted by verified ateliers</span>
          <span className="sm:hidden">Free shipping ₹1,999+ · Verified ateliers</span>
        </div>
      ) : (
        // No announcement bar — still need to reserve the iOS status-bar
        // height so the logo / SIGN IN row isn't clipped. Paints the
        // header root's own bg through the inset gap.
        <div aria-hidden className="safe-area-top" />
      )}

      <div className="px-4 md:px-8 lg:px-16 py-4 flex items-center justify-between gap-4 md:gap-6">
        <div className="flex items-center gap-2 md:gap-4">
          <button
            className={`md:hidden p-2 -ml-2 ${styles.iconColor}`}
            onClick={() => setOpenMenu(true)}
            data-testid="mobile-menu-btn"
            aria-label="Open menu"
          >
            <Menu size={20} />
          </button>
          <Link to="/" className={styles.logo} data-testid="logo-link">
            {branding?.logo_horizontal_url ? (
              <img src={imgUrl(branding.logo_horizontal_url)} alt={branding.brand_name} className="h-8 md:h-10 w-auto object-contain" />
            ) : branding?.logo_square_url ? (
              <>
                <img src={imgUrl(branding.logo_square_url)} alt="" className="h-8 md:h-10 w-auto object-contain" />
                <span>{branding.brand_name}</span>
              </>
            ) : (
              <>{(branding?.brand_name || "KalkiBazaar")}</>
            )}
          </Link>
        </div>

        <form
          onSubmit={onSearch}
          className={styles.searchWrap}
        >
          <Search size={16} className={`${styles.iconColor || "text-[hsl(var(--kb-muted))]"}`} aria-hidden="true" />
          <input
            data-testid="search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search sarees, lehengas, ateliers..."
            className={styles.searchInput}
          />
        </form>

        <div className="flex items-center gap-3 md:gap-5">
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger
                className="flex items-center gap-2 text-sm focus:outline-none"
                data-testid="user-menu-btn"
                aria-label="Account menu"
              >
                <User size={18} />
                <span className="hidden lg:inline max-w-[120px] truncate">{user.name || user.phone}</span>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 rounded-none border-[hsl(var(--kb-border))]">
                <DropdownMenuItem asChild>
                  <Link to="/account" data-testid="menu-account">My Account</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/wishlist">Wishlist</Link>
                </DropdownMenuItem>
                {user.role === "seller" && (
                  <DropdownMenuItem asChild>
                    <Link to="/seller" data-testid="menu-seller">Seller Dashboard</Link>
                  </DropdownMenuItem>
                )}
                {user.role === "customer" && (
                  <DropdownMenuItem asChild>
                    <Link to="/seller/onboard" data-testid="menu-become-seller">Become a Seller</Link>
                  </DropdownMenuItem>
                )}
                {user.role === "admin" && (
                  <DropdownMenuItem asChild>
                    <Link to="/admin" data-testid="menu-admin">Admin Console</Link>
                  </DropdownMenuItem>
                )}
                {user.role === "super_admin" && (
                  <DropdownMenuItem asChild>
                    <Link to="/superadmin" data-testid="menu-super-admin">Super Admin Console</Link>
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={onLogout} data-testid="logout-btn" className="cursor-pointer">
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <button
              onClick={() => setOpenAuth(true)}
              className="text-xs md:text-sm uppercase tracking-wider font-medium"
              data-testid="login-btn"
            >
              Sign in
            </button>
          )}

          <Link to="/wishlist" aria-label="Wishlist" data-testid="header-wishlist-link" className="hidden sm:inline-flex relative">
            <Heart size={20} />
            {wishlistCount > 0 && (
              <span
                className="absolute -top-2 -right-2 bg-[hsl(var(--kb-primary))] text-white text-[10px] min-w-[18px] h-[18px] flex items-center justify-center rounded-full px-1"
                data-testid="wishlist-count"
              >
                {wishlistCount}
              </span>
            )}
          </Link>

          <Link to="/cart" aria-label="Cart" data-testid="header-cart-link" className="relative">
            <ShoppingBag size={20} />
            {cartCount > 0 && (
              <span
                className="absolute -top-2 -right-2 bg-[hsl(var(--kb-primary))] text-white text-[10px] min-w-[18px] h-[18px] flex items-center justify-center rounded-full px-1"
                data-testid="cart-count"
              >
                {cartCount}
              </span>
            )}
          </Link>
        </div>
      </div>

      {/* Department mega-menu nav */}
      <nav
        className={styles.nav}
        data-testid="mega-nav"
        onMouseLeave={() => setHovered(null)}
      >
        {departments.map((dept) => (
          <div
            key={dept.id}
            className="relative"
            onMouseEnter={() => setHovered(dept.slug)}
          >
            <NavLink
              to={`/shop/${dept.slug}`}
              data-testid={`nav-${dept.slug}`}
              className={({ isActive }) =>
                `${styles.navLink} ${isActive || hovered === dept.slug ? styles.navLinkActive : ""}`
              }
            >
              {dept.name}
              {(dept.children?.length || 0) > 0 && <ChevronDown size={12} />}
            </NavLink>
          </div>
        ))}
        <NavLink
          to="/reels"
          data-testid="storefront-nav-reels"
          onMouseEnter={() => setHovered(null)}
          className={({ isActive }) =>
            `${styles.navLink} ${isActive ? styles.navLinkActive : ""} ${reelsEnabled ? "" : "hidden"}`
          }
        >
          Reels
        </NavLink>
        {hovered && departments.find((d) => d.slug === hovered) &&
          renderMegaPanel(departments.find((d) => d.slug === hovered))}
      </nav>

      <PhoneOtpModal open={openAuth} onOpenChange={setOpenAuth} />

      <Sheet open={openMenu} onOpenChange={setOpenMenu}>
        <SheetContent side="left" className="w-80 p-6 overflow-y-auto">
          <SheetHeader className="sr-only">
            <SheetTitle>Navigation menu</SheetTitle>
            <SheetDescription>Browse categories and account</SheetDescription>
          </SheetHeader>
          <div className="font-display text-2xl mb-6">{branding?.brand_name || "KalkiBazaar"}</div>
          <form onSubmit={onSearch} className="flex items-center gap-2 border-b border-[hsl(var(--kb-border))] mb-6">
            <Search size={16} className="text-[hsl(var(--kb-muted))]" aria-hidden="true" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search the bazaar"
              className="flex-1 bg-transparent py-2 text-sm focus:outline-none"
              data-testid="mobile-search-input"
            />
          </form>
          <div className="flex flex-col gap-5">
            {departments.map((dept) => (
              <div key={dept.id}>
                <Link
                  to={`/shop/${dept.slug}`}
                  onClick={() => setOpenMenu(false)}
                  className="text-sm uppercase tracking-widest font-medium block mb-2"
                  data-testid={`mobile-nav-${dept.slug}`}
                >
                  {dept.name}
                </Link>
                {(dept.children || []).slice(0, 6).map((c) => (
                  <Link
                    key={c.id}
                    to={`/shop/${c.slug}`}
                    onClick={() => setOpenMenu(false)}
                    className="block text-xs text-[hsl(var(--kb-muted))] py-1 pl-3"
                  >
                    {c.name}
                  </Link>
                ))}
              </div>
            ))}
            <div className="h-px bg-[hsl(var(--kb-border))] my-3" />
            {reelsEnabled && (
              <Link to="/reels" onClick={() => setOpenMenu(false)} className="text-sm uppercase tracking-widest" data-testid="mobile-nav-reels">
                Reels
              </Link>
            )}
            <Link to="/wishlist" onClick={() => setOpenMenu(false)} className="text-sm uppercase tracking-widest">
              Wishlist
            </Link>
            {user && (
              <Link to="/account" onClick={() => setOpenMenu(false)} className="text-sm uppercase tracking-widest">
                My Account
              </Link>
            )}
            {user?.role === "seller" && (
              <Link to="/seller" onClick={() => setOpenMenu(false)} className="text-sm uppercase tracking-widest">
                Seller Dashboard
              </Link>
            )}
            {user?.role === "admin" && (
              <Link to="/admin" onClick={() => setOpenMenu(false)} className="text-sm uppercase tracking-widest">
                Admin Console
              </Link>
            )}
            {user?.role === "super_admin" && (
              <Link to="/superadmin" onClick={() => setOpenMenu(false)} className="text-sm uppercase tracking-widest" data-testid="mobile-menu-super-admin">
                Super Admin Console
              </Link>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </header>
  );
}
